import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getFormArray } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { controlloCaricaPresidente, formArrayRequiredHelper } from '../common/helpers/norme-validators.helper';
import { codiceFiscaleHelper, futureOrInconsistentDate, ruleGuard } from './amm-controllo-validators.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

const CONS_AMM_ARRAY_PATH = `${formPaths.ammControllo}.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione`;

const my_AllElemConsiglioAmministrazioneRequiredHelper = (form: FormGroup, targetPath: string) => {
    const my_AllElemConsiglioAmministrazioneFormArray = getFormArray(form, CONS_AMM_ARRAY_PATH);
    formArrayRequiredHelper(my_AllElemConsiglioAmministrazioneFormArray, targetPath);
};

export const consiglioAmministrazioneRules = (ckNormeTargetValue: CkNormeValues) => ({
    my_AllConsAmmDataElezione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            const my_AllConsAmmFormArray = getFormArray(form, CONS_AMM_ARRAY_PATH);

            my_AllConsAmmFormArray.controls.map((group: FormGroup) => {
                const my_AllConsAmmDataElezioneControl = getControl(group, 'my_AllConsAmmDataElezione');
                const errors = my_AllConsAmmDataElezioneControl.errors ? my_AllConsAmmDataElezioneControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllConsAmmDataElezioneControl?.value), errors, 'my_AllConsAmmDataElezione', {
                    status: REQUIRED_STATUS,
                });

                my_AllConsAmmDataElezioneControl.setErrors(isEqual(errors, {}) ? null : errors);
                futureOrInconsistentDate(form, my_AllConsAmmDataElezioneControl, 'my_AllConsAmmDataElezione', ckNormeTargetValue);
            });
        });
    },

    my_AllConsAmmCarica: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            const my_AllElemConsiglioAmministrazioneFormArray = getFormArray(form, CONS_AMM_ARRAY_PATH);
            formArrayRequiredHelper(my_AllElemConsiglioAmministrazioneFormArray, 'my_AllConsAmmCarica');
            controlloCaricaPresidente(my_AllElemConsiglioAmministrazioneFormArray, 'my_AllConsAmmCarica');
        });
    },

    my_AllConsAmmCognome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmCognome');
        });
    },

    my_AllConsAmmNome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmNome');
        });
    },

    my_AllConsAmmVia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmVia');
        });
    },

    my_AllConsAmmProvincia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmProvincia');
        });
    },

    my_AllConsAmmCap: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmCap');
        });
    },

    my_AllConsAmmComune: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            my_AllElemConsiglioAmministrazioneRequiredHelper(form, 'my_AllConsAmmComune');
        });
    },

    my_AllConsAmmCodFisc: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario, CkNormeValuesEnum.normeSpaMonistico], () => {
            const my_AllElemConsiglioAmministrazioneFormArray = getFormArray(form, CONS_AMM_ARRAY_PATH);

            my_AllElemConsiglioAmministrazioneFormArray.controls.forEach((group: FormGroup) => {
                const my_AllConsAmmCodFiscControl = getControl(group, 'my_AllConsAmmCodFisc');
                const errors = my_AllConsAmmCodFiscControl.errors ? my_AllConsAmmCodFiscControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllConsAmmCodFiscControl.value), errors, 'my_AllConsAmmCodFisc', {
                    status: REQUIRED_STATUS,
                });

                my_AllConsAmmCodFiscControl.setErrors(isEqual(errors, {}) ? null : errors);
                codiceFiscaleHelper(group, 'my_AllConsAmmNome', 'my_AllConsAmmCognome', 'my_AllConsAmmCodFisc');
            });
        });
    },
});
